/**
 * Audio API Client
 * Provides type-safe functions for interacting with audio storage and playback endpoints
 */
import { api } from '../services/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/**
 * Audio session metadata
 */
export interface AudioSession {
  session_id: string;
  patient_id?: string;
  patient_name?: string | null;
  mrn?: string | null;
  visit_id?: number | null;
  visit_date?: string | null;
  session_date: string | null;
  session_time: string | null;
  created_at?: string | null; // ISO string for sorting recent first
  chunk_count: number;
  duration: number; // in seconds
  audio_url: string;
}

/**
 * Sessions grouped by patient ID
 */
export interface SessionsByPatient {
  [patient_id: string]: AudioSession[];
}

/**
 * Audio metadata response
 */
export interface AudioMetadata {
  session_id: string;
  audio_url: string;
  status: 'cached' | 'generated';
  file_size: number;
  duration_ms?: number;
  chunks_processed?: number;
  total_chunks?: number;
  cached_at?: string;
  generated_at?: string;
}

/**
 * Audio segment with speaker and timing info
 */
export interface AudioSegment {
  id: string;
  speaker: string;
  text: string;
  startTime: number;
  endTime: number;
  confidence?: number;
}

/**
 * Segments response
 */
export interface AudioSegmentsResponse {
  session_id: string;
  segments: AudioSegment[];
  total_segments: number;
  total_duration: number;
}

/**
 * Get list of all sessions with audio recordings
 * Grouped by patient ID
 */
export const getSessionsWithAudio = async (filters?: {
  patient_name?: string;
  mrn?: string;
  session_id?: string;
  visit_id?: string;
  visit_date?: string;
}): Promise<SessionsByPatient> => {
  try {
    const response = await api.get('/api/audio/sessions/list', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch sessions with audio:', error);
    throw error;
  }
};

/**
 * Get audio metadata for a specific session
 * This will trigger audio generation if not already cached
 */
export const getSessionAudio = async (sessionId: string, regenerate: boolean = false): Promise<AudioMetadata> => {
  try {
    const response = await api.get(`/api/audio/session/${sessionId}`, {
      params: { regenerate }
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch audio for session ${sessionId}:`, error);
    throw error;
  }
};

/**
 * Get audio file URL for playback
 * Returns the direct URL to the WAV file
 */
export const getAudioFileUrl = (sessionId: string): string => {
  return `${API_BASE_URL}/api/audio/file/${sessionId}`;
};

/**
 * Get speaker-labeled transcript segments with timestamps
 * Useful for timeline-based audio player
 */
export const getAudioSegments = async (sessionId: string): Promise<AudioSegmentsResponse> => {
  try {
    const response = await api.get(`/api/audio/segments/${sessionId}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch segments for session ${sessionId}:`, error);
    throw error;
  }
};

/**
 * Explicitly trigger audio generation for a session
 * Useful for pre-generating audio files in the background
 */
export const generateAudio = async (sessionId: string): Promise<AudioMetadata> => {
  try {
    const response = await api.post(`/api/audio/generate/${sessionId}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to generate audio for session ${sessionId}:`, error);
    throw error;
  }
};

/**
 * Clear cached audio file for a session
 * Next request will regenerate the file
 */
export const clearAudioCache = async (sessionId: string): Promise<void> => {
  try {
    await api.delete(`/api/audio/cache/${sessionId}`);
  } catch (error) {
    console.error(`Failed to clear audio cache for session ${sessionId}:`, error);
    throw error;
  }
};

/**
 * Get cache statistics
 * Returns total files, disk usage, and list of cached sessions
 */
export const getCacheStats = async (): Promise<{
  total_files: number;
  total_size_bytes: number;
  total_size_mb: number;
  sessions: Array<{
    session_id: string;
    file_size: number;
    cached_at: string;
  }>;
}> => {
  try {
    const response = await api.get('/api/audio/cache/stats');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch cache stats:', error);
    throw error;
  }
};
